/**
 * FestiveBanner — V1.9.18
 *
 * Slim, dismissible celebration banner for temporary campaigns
 * (Independence Day, Diwali, New Year, etc.). Sits above the landing
 * page hero. Auto-hides after `endDate` passes so a forgotten banner
 * never lingers into next month.
 *
 * To remove: simply delete `<FestiveBanner />` from LandingPage.jsx,
 * or push the end date into the past.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkle } from "@phosphor-icons/react";

// Change these three constants to switch campaigns. Setting `endDate`
// in the past hides the banner entirely — no code deletion needed.
const CAMPAIGN = {
    id: "independence-2026-aug",  // localStorage dismiss key namespace
    startDate: "2026-08-12T00:00:00+05:30",
    endDate: "2026-08-19T23:59:59+05:30",   // ~week around Aug 15
    theme: "independence-in",
    eyebrow: "🇮🇳 Independence Week Special",
    headline: "Hire India's brightest — DaddyTalk chats FREE for 6 days",
    subline: "Celebrate freedom to hire, freedom to switch. Zero fees till Aug 19.",
    ctaLabel: "Start chatting",
    ctaHref: "/daddytalk",
};

const DISMISS_KEY = `festive_banner_dismissed_${CAMPAIGN.id}`;

function withinWindow() {
    const now = Date.now();
    const start = new Date(CAMPAIGN.startDate).getTime();
    const end = new Date(CAMPAIGN.endDate).getTime();
    return now >= start && now <= end;
}

export default function FestiveBanner() {
    const [dismissed, setDismissed] = useState(() => {
        try {
            return !!localStorage.getItem(DISMISS_KEY);
        } catch {
            return false;
        }
    });

    if (!withinWindow()) return null;
    if (dismissed) return null;

    const dismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            /* ignore */
        }
        setDismissed(true);
    };

    return (
        <div
            data-testid="festive-banner"
            className="relative bg-gradient-to-r from-[#FF9933] via-[#FFFFFF] to-[#138808] overflow-hidden border-b border-ink/10"
        >
            {/* Chakra silhouette accent */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 flex items-center justify-center opacity-15"
            >
                <div className="w-16 h-16 rounded-full border-4 border-[#000080] relative">
                    {Array.from({ length: 24 }).map((_, i) => (
                        <div
                            key={i}
                            className="absolute left-1/2 top-1/2 w-[1px] h-8 bg-[#000080]"
                            style={{
                                transformOrigin: "top center",
                                transform: `translate(-50%, -50%) rotate(${i * 15}deg) translateY(-16px)`,
                            }}
                        />
                    ))}
                </div>
            </div>

            <div className="relative max-w-6xl mx-auto px-4 py-2.5 sm:py-3 flex items-center gap-3 z-10">
                <Sparkle size={14} weight="fill" className="text-[#000080] shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] text-[#000080]">
                        {CAMPAIGN.eyebrow}
                    </p>
                    <p className="text-xs sm:text-sm font-black text-ink leading-tight truncate">
                        {CAMPAIGN.headline}
                    </p>
                    <p className="hidden sm:block text-[11px] text-ink/70 mt-0.5">
                        {CAMPAIGN.subline}
                    </p>
                </div>
                <Link
                    to={CAMPAIGN.ctaHref}
                    data-testid="festive-banner-cta"
                    className="shrink-0 bg-[#000080] hover:bg-[#000060] text-white px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-[11px] font-black uppercase tracking-wider transition-colors"
                >
                    {CAMPAIGN.ctaLabel}
                </Link>
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Dismiss festive banner"
                    data-testid="festive-banner-dismiss"
                    className="shrink-0 text-ink/50 hover:text-ink p-1"
                >
                    <X size={14} weight="bold" />
                </button>
            </div>
        </div>
    );
}
